import { useState, useEffect } from 'react';
import httpClient from 'API/httpClient';
import { ProductItem, SearchFilter, SearchResponse, CategoryFacet } from 'types/item';
import { GroupIdResponse } from 'types/group';
import { ApiError } from 'types/apiError';

interface TileChildrenResponse {
  Children?: { id: number }[];
}

const useProductData = (
  categoryName?: string | null,
  partNumber?: string | null,
  filters?: SearchFilter[] | null,
  searchInput?: string | null,
  itemcode?: string | null,
  restrictByParentIds: number[] | null = null,
) => {
  const [data, setData] = useState<ProductItem[]>([]);
  const [total, setTotal] = useState(0);
  const [categoryFacets, setCategoryFacets] = useState<CategoryFacet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const trimmedSearch = String(searchInput ?? '').trim();
    const hasNonEmptyFilters = Array.isArray(filters) && filters.length > 0;
    const isGlobalSuggestionOnly =
      !categoryName && !partNumber && !itemcode && !hasNonEmptyFilters;

    if (isGlobalSuggestionOnly && !trimmedSearch) {
      setData([]);
      setTotal(0);
      setCategoryFacets([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    const getProductsApi = async (): Promise<SearchResponse<ProductItem>> => {
      let searchFilters: SearchFilter[] = [];

      let categoryCode: number | null = null;
      if (categoryName) {
        try {
          const groupResponse = await httpClient.get<GroupIdResponse>(
            `/group/${categoryName}/id`,
          );
          categoryCode = groupResponse.data.groupId;
        } catch (error) {
          console.error('Error fetching category code:', error);
        }
      }

      if (filters) {
        searchFilters = [...filters];
      }

      if (trimmedSearch) {
        searchFilters.push({
          columnName: 'Search',
          value: trimmedSearch,
          operator: 'contains',
        });
      }

      if (itemcode) {
        searchFilters.push({
          columnName: 'ItemCode',
          value: itemcode,
          operator: 'eq',
        });
      }

      if (categoryCode && categoryCode !== -1) {
        searchFilters.push({
          columnName: 'Group',
          value: categoryCode,
          operator: 'eq',
        });
      }

      if (
        Array.isArray(restrictByParentIds) &&
        restrictByParentIds.length > 0 &&
        !categoryCode
      ) {
        try {
          const idsParam = restrictByParentIds.join(',');
          const tilesResponse = await httpClient.get<TileChildrenResponse[]>(
            `/tiles/${idsParam}`,
          );
          const childIds = (tilesResponse.data ?? [])
            .flatMap((parent) => parent?.Children ?? [])
            .map((child) => child?.id)
            .filter((id): id is number => id != null);

          if (childIds.length > 0) {
            searchFilters.push({
              columnName: 'Group',
              value: childIds,
              operator: 'in',
            });
          }
        } catch (error) {
          console.error(
            'useProductData: failed to resolve parent category children',
            error,
          );
        }
      }

      const response = await httpClient.post<SearchResponse<ProductItem>>(
        '/search',
        searchFilters,
      );
      return response.data;
    };

    const timeoutId = setTimeout(async () => {
      try {
        const {
          items,
          total: resultTotal,
          categoryFacets: resultCategoryFacets,
        } = await getProductsApi();
        if (!cancelled) {
          setData(items);
          setTotal(resultTotal);
          setCategoryFacets(resultCategoryFacets ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err as ApiError);
          setData([]);
          setTotal(0);
          setCategoryFacets([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      setLoading(false);
    };
  }, [
    categoryName,
    partNumber,
    filters,
    searchInput,
    itemcode,
    restrictByParentIds,
  ]);

  return { data, total, categoryFacets, loading, error };
};

export default useProductData;
